import React from 'react';
import { Code, Database, Cloud, Brain } from 'lucide-react';
import GlassCard from '../ui/GlassCard';
import GlowingText from '../ui/GlowingText';

const SkillsSection = ({ skills }) => {
  const skillCategories = [
    {
      icon: Code,
      color: 'from-violet-500 to-purple-500',
      category: 'Frontend Development',
      skills: skills['Frontend Development'] || []
    },
    {
      icon: Database,
      color: 'from-blue-500 to-cyan-500',
      category: 'Backend Development', 
      skills: skills['Backend Development'] || []
    },
    {
      icon: Cloud,
      color: 'from-emerald-500 to-teal-500',
      category: 'Database & Cloud',
      skills: skills['Database & Cloud'] || []
    },
    {
      icon: Brain,
      color: 'from-amber-500 to-orange-500',
      category: 'AI & Modern Tools',
      skills: skills['AI & Modern Tools'] || []
    }
  ];

  return (
    <section id="skills" className="py-32 px-6 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-violet-500/10 to-purple-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center mb-20">
          <span className="text-caption text-violet-400 mb-4 block">What I Do</span>
          <h2 className="text-display-lg mb-6">            <GlowingText 
              color="mixed"
              size="3xl"
              className="bg-gradient-to-r from-white via-violet-200 to-blue-200 bg-clip-text text-transparent"
            >
              Skills & Expertise
            </GlowingText>
          </h2>
          <p className="text-body-lg text-neutral-300 max-w-2xl mx-auto">
            I specialize in building modern, scalable applications using cutting-edge 
            technologies and best practices in software development.
          </p>
        </div>

        {/* Skills Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {skillCategories.map((category, categoryIndex) => (
            <GlassCard 
              key={category.category}
              variant="bordered"
              color="mixed"
              glow={true}
              glowIntensity="subtle"
              hoverEffect="lift"
              className="p-6 h-full group"
              style={{ animationDelay: `${categoryIndex * 100}ms` }}
            >
              {/* Category Header */}
              <div className="mb-6">
                <div className={`inline-flex p-3 rounded-xl bg-gradient-to-r ${category.color} bg-opacity-20 mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  <category.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-heading-md text-white mb-2 group-hover:text-violet-300 transition-colors">
                  {category.category}
                </h3>
              </div>

              {/* Skills List */}
              <div className="space-y-4">
                {category.skills.map((skill, skillIndex) => (
                  <div key={skill.name} className="group/skill">
                    {/* Skill Info */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{skill.icon}</span>
                        <span className="text-sm font-medium text-neutral-200 group-hover/skill:text-white transition-colors">
                          {skill.name}
                        </span>
                      </div>
                      <span className="text-xs text-neutral-400 font-mono">
                        {skill.level}%
                      </span>
                    </div>

                    {/* Progress Bar */}
                    <div className="relative">
                      <div className="h-1.5 bg-neutral-800 rounded-full overflow-hidden">
                        <div 
                          className={`h-full bg-gradient-to-r ${skill.color} rounded-full transition-all duration-1000 ease-out`}
                          style={{ 
                            width: `${skill.level}%`,
                            animationDelay: `${(categoryIndex * 200) + (skillIndex * 100)}ms`
                          }}
                        ></div>
                      </div>
                      {/* Glow effect */}
                      <div 
                        className={`absolute top-0 h-1.5 bg-gradient-to-r ${skill.color} rounded-full opacity-50 blur-sm transition-all duration-1000 ease-out`}
                        style={{ 
                          width: `${skill.level}%`,
                          animationDelay: `${(categoryIndex * 200) + (skillIndex * 100)}ms`
                        }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Decorative Element */}
              <div className="absolute top-4 right-4 w-2 h-2 bg-gradient-to-r from-violet-400 to-blue-400 rounded-full opacity-60 group-hover:opacity-100 transition-opacity"></div>
            </GlassCard>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-gradient-to-r from-violet-500/10 to-blue-500/10 border border-violet-500/20">
            <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
            <span className="text-sm text-neutral-300">
              Always learning and exploring new technologies
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SkillsSection;
